import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export const ALL_PACKAGES = [
  {
    key: 'freshGoods',
    label: 'Fresh Goods',
    icon: '🥦',
    desc: 'Johannesburg & Pretoria produce market data',
    unlocked: true,
  },
  {
    key: 'marketResearch',
    label: 'Market Research',
    icon: '📊',
    desc: 'Consumer & industry research reports',
    unlocked: true,
  },
  {
    key: 'automotive',
    label: 'Automotive',
    icon: '🚗',
    desc: 'Vehicle sales, dealership & fleet data',
    unlocked: false,
  },
  {
    key: 'realEstate',
    label: 'Real Estate',
    icon: '🏢',
    desc: 'Property market intelligence & valuations',
    unlocked: false,
  },
  {
    key: 'energy',
    label: 'Energy',
    icon: '⚡',
    desc: 'Fuel prices & energy consumption analytics',
    unlocked: false,
  },
  {
    key: 'technology',
    label: 'Technology',
    icon: '💻',
    desc: 'Tech sector trends & adoption metrics',
    unlocked: false,
  },
];

export const DEFAULT_SUBSCRIPTIONS = {
  freshGoods: false,
  marketResearch: false,
  automotive: false,
  realEstate: false,
  energy: false,
  technology: false,
};

// subscriptionMeta holds per-package date/plan info:
// { freshGoods: { activatedAt, expiresAt, planType, cancelledAt } }
export const DEFAULT_META = {};

export const DEFAULT_WIDGETS = {
  priceChart: true,
  volumeChart: true,
  commodityBreakdown: true,
  marketComparison: false,
  weeklyTrend: false,
  researchIndex: false,
};

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const [subscriptions, setSubscriptions] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('tdx_subscriptions')) || DEFAULT_SUBSCRIPTIONS;
    } catch {
      return DEFAULT_SUBSCRIPTIONS;
    }
  });

  const [subscriptionMeta, setSubscriptionMeta] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('tdx_subscription_meta')) || DEFAULT_META;
    } catch {
      return DEFAULT_META;
    }
  });

  const [dashboardWidgets, setDashboardWidgets] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('tdx_widgets')) || DEFAULT_WIDGETS;
    } catch {
      return DEFAULT_WIDGETS;
    }
  });

  // ── Load subscriptions from Supabase ──────────────────────
  const loadFromDB = useCallback(async () => {
    if (!supabase) return;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;

    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', session.user.id);
    console.log('[Subscriptions] loadFromDB:', { data, error });

    if (error || !data) return;

    const subs = { ...DEFAULT_SUBSCRIPTIONS };
    const meta = {};

    data.forEach((row) => {
      if (row.package_key in subs) {
        subs[row.package_key] = row.active === true || row.paid === true;
        meta[row.package_key] = {
          activatedAt: row.activated_at,
          expiresAt: row.expires_at,
          planType: row.plan_type || 'monthly',
          cancelledAt: row.cancelled_at,
        };
      }
    });

    setSubscriptions(subs);
    setSubscriptionMeta(meta);
    localStorage.setItem('tdx_subscriptions', JSON.stringify(subs));
    localStorage.setItem('tdx_subscription_meta', JSON.stringify(meta));
  }, []);

  // ── Sync on mount & auth changes ──────────────────────────
  useEffect(() => {
    loadFromDB();

    if (!supabase) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        loadFromDB();
      } else if (event === 'SIGNED_OUT') {
        setSubscriptions(DEFAULT_SUBSCRIPTIONS);
        setSubscriptionMeta(DEFAULT_META);
        localStorage.removeItem('tdx_subscriptions');
        localStorage.removeItem('tdx_subscription_meta');
      }
    });
    return () => subscription?.unsubscribe();
  }, [loadFromDB]);

  // ── Toggle / update subscriptions ─────────────────────────
  // metaOverrides: optional { [key]: { activatedAt, expiresAt, planType, cancelledAt } }
  const updateSubscriptions = async (updates, metaOverrides = {}) => {
    const next = { ...subscriptions, ...updates };
    setSubscriptions(next);
    localStorage.setItem('tdx_subscriptions', JSON.stringify(next));

    const now = new Date().toISOString();

    // Build next meta
    const nextMeta = { ...subscriptionMeta };
    Object.entries(updates).forEach(([key, isActive]) => {
      const override = metaOverrides[key] || {};
      if (isActive) {
        nextMeta[key] = {
          activatedAt: nextMeta[key]?.activatedAt || override.activatedAt || now,
          expiresAt: override.expiresAt || nextMeta[key]?.expiresAt || null,
          planType: override.planType || nextMeta[key]?.planType || 'monthly',
          cancelledAt: null,
        };
      } else {
        nextMeta[key] = {
          ...nextMeta[key],
          cancelledAt: override.cancelledAt || now,
        };
      }
    });
    setSubscriptionMeta(nextMeta);
    localStorage.setItem('tdx_subscription_meta', JSON.stringify(nextMeta));

    // Persist to Supabase
    if (!supabase) return;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;

    const upserts = Object.entries(updates).map(([key, isActive]) => {
      const m = nextMeta[key] || {};
      return {
        user_id: session.user.id,
        package_key: key,
        active: isActive,
        paid: isActive,
        activated_at: m.activatedAt || null,
        expires_at: m.expiresAt || null,
        updated_at: now,
      };
    });

    const { error } = await supabase
      .from('user_subscriptions')
      .upsert(upserts, { onConflict: 'user_id,package_key' });

    if (error) console.warn('[Subscriptions] upsert error:', error.message);
  };

  const updateWidgets = (updates) => {
    const next = { ...dashboardWidgets, ...updates };
    setDashboardWidgets(next);
    localStorage.setItem('tdx_widgets', JSON.stringify(next));
  };

  const activeCount = Object.values(subscriptions).filter(Boolean).length;

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptions,
        subscriptionMeta,
        updateSubscriptions,
        dashboardWidgets,
        updateWidgets,
        activeCount,
        allPackages: ALL_PACKAGES,
        reloadSubscriptions: loadFromDB,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
